'use client';

import { useFormatter } from 'next-intl';
import type { ReactNode } from 'react';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { useSearch } from '../core/SearchContext';
import { ROW_STAGGER_CLASS, SECTION_ENTER_CLASS } from './motion';
import { SearchLink } from './SearchLink';

const ACTION_CLASS =
  'flex h-[22px] shrink-0 cursor-pointer items-center gap-0.5 rounded-[6px] pr-1 pl-1.5 text-[11.5px] leading-none text-v2-text-secondary transition-colors hover:bg-v2-neutral-surface hover:text-v2-text-primary dark:hover:bg-v2-surface-tertiary';

interface SectionActionProps {
  label: string;
  /** A page to open… */
  href?: string;
  /** …or something to do here, like switching to the category's tab. */
  onClick?: () => void;
}

/** "See all ›" / "Leaderboard ›" on a section's header. */
export function SectionAction({ label, href, onClick }: SectionActionProps) {
  const { close } = useSearch();
  // A page opened from here leaves the search behind, as a result does — a
  // soft navigation in the same zone would otherwise keep the panel up.
  const handleNavigate = () => {
    onClick?.();
    close();
  };
  const content = (
    <>
      {label}
      <span aria-hidden className="flex size-3 items-center justify-center">
        <Icon name="ArrowSmall" size={6} />
      </span>
    </>
  );
  if (href) {
    return (
      <SearchLink href={href} onClick={handleNavigate} className={ACTION_CLASS}>
        {content}
      </SearchLink>
    );
  }
  return (
    <button type="button" onClick={onClick} className={ACTION_CLASS}>
      {content}
    </button>
  );
}

interface SectionProps {
  title: string;
  /** How many the category found — a query's sections only. */
  count?: number;
  action?: ReactNode;
  children: ReactNode;
}

/** A titled block of rows: a quiet caption with its count and action, the rows bare under it. */
export function Section({ title, count, action, children }: SectionProps) {
  const format = useFormatter();
  return (
    <section className={cn('flex flex-col', SECTION_ENTER_CLASS)}>
      <div className="flex h-[26px] items-center justify-between gap-3 pr-1 pl-2">
        <h3 className="flex items-center gap-1.5 text-[11.5px] font-medium leading-none text-v2-text-secondary">
          {title}
          {count == null ? null : (
            <span className="tabular-nums opacity-60">{format.number(count)}</span>
          )}
        </h3>
        {action}
      </div>
      <ul role="group" aria-label={title} className={cn('flex flex-col', ROW_STAGGER_CLASS)}>
        {children}
      </ul>
    </section>
  );
}
